<template>
  <v-card outlined class="mt-6">
    <v-card-title class="text-subtitle-1">
      Rechnungsnummern
    </v-card-title>
    <v-card-text>
      <p class="text-caption text--secondary mb-3">
        Hier wird die zuletzt vergebene automatische Rechnungsnummer festgelegt.
        Bereits reservierte Nummern werden nicht erneut verwendet.
      </p>
      <v-alert
        v-if="fehlermeldung"
        dense
        text
        type="error"
        class="mb-4"
      >
        {{ fehlermeldung }}
        <div v-if="revision === ''" class="mt-2">
          <v-btn small text color="error" @click="laden">
            Erneut laden
          </v-btn>
        </div>
      </v-alert>
      <v-form
        ref="formular"
        v-model="formularGueltig"
        @submit.prevent="speichern"
      >
        <v-text-field
          v-model.trim="letzteNummer"
          :rules="nummernRegeln"
          :loading="laedt"
          :disabled="laedt || speichert || revision === ''"
          label="Aktueller Rechnungsnummernstand"
          prefix="RE-"
          inputmode="numeric"
          autocomplete="off"
          maxlength="12"
          counter="12"
          hint="Trage die Nummer der zuletzt vergebenen Rechnung ohne RE- ein."
          persistent-hint
        />
      </v-form>
      <p class="text-body-2 mt-3 mb-0">
        {{ naechsteNummerTitel }}:
        <strong>RE-{{ vorschauNaechsteNummer }}</strong>
      </p>
      <v-alert
        dense
        text
        type="warning"
        class="mt-4 mb-0"
      >
        Ändere den Stand nur nach Prüfung der zuletzt ausgestellten Rechnung.
        Bereits erzeugte Rechnungen und ihre Nummern bleiben unverändert.
      </v-alert>
    </v-card-text>
    <v-card-actions class="px-4 pb-4">
      <v-spacer />
      <v-btn
        color="success"
        small
        :loading="speichert"
        :disabled="!speichernMoeglich"
        @click="speichern"
      >
        <v-icon left>mdi-content-save</v-icon>
        Rechnungsnummernstand speichern
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
const MAX_RECHNUNGSNUMMERNSTAND = 999999999998;

export default {
  name: 'RechnungsnummerEinstellungen',
  data: () => ({
    laedt: false,
    speichert: false,
    formularGueltig: false,
    letzteNummer: '',
    geladeneLetzteNummer: '',
    naechsteNummer: '',
    revision: '',
    fehlermeldung: '',
    aktiveAnfrage: null,
    wirdZerstoert: false,
  }),
  computed: {
    nummernRegeln() {
      return [
        (wert) => /^\d{1,12}$/.test(String(wert || ''))
          || 'Bitte 1 bis 12 Ziffern eingeben.',
        (wert) => {
          const nummer = Number(wert);
          return (Number.isSafeInteger(nummer)
            && nummer > 0
            && nummer <= MAX_RECHNUNGSNUMMERNSTAND)
            || 'Der Rechnungsnummernstand ist ungültig oder zu hoch.';
        },
      ];
    },
    vorschauNaechsteNummer() {
      if (
        this.letzteNummer === this.geladeneLetzteNummer
        && this.naechsteNummer !== ''
      ) {
        return this.naechsteNummer;
      }
      if (!/^\d{1,12}$/.test(String(this.letzteNummer || ''))) return '–';
      const nummer = Number(this.letzteNummer);
      if (!Number.isSafeInteger(nummer)
        || nummer <= 0
        || nummer > MAX_RECHNUNGSNUMMERNSTAND) return '–';
      return String(nummer + 1);
    },
    naechsteNummerTitel() {
      return this.letzteNummer === this.geladeneLetzteNummer
        ? 'Nächste automatische Rechnungsnummer'
        : 'Voraussichtliche nächste Rechnungsnummer';
    },
    speichernMoeglich() {
      return !this.laedt
        && !this.speichert
        && this.formularGueltig
        && this.revision !== ''
        && this.letzteNummer !== this.geladeneLetzteNummer;
    },
  },
  methods: {
    statusUebernehmen(status) {
      if (!status || typeof status !== 'object') return;
      this.letzteNummer = String(status.letzteNummer || '');
      this.geladeneLetzteNummer = this.letzteNummer;
      this.naechsteNummer = String(status.naechsteNummer || '');
      this.revision = String(status.revision || '');
      this.$nextTick(() => {
        if (this.$refs.formular) this.$refs.formular.resetValidation();
      });
    },
    async anfragen(aktion) {
      const formular = new FormData();
      formular.append('code', this.$store.state.login.session.code);
      formular.append('aktion', aktion);
      if (aktion === 'speichern') {
        formular.append('letzteNummer', this.letzteNummer);
        formular.append('revision', this.revision);
      }

      const controller = typeof AbortController !== 'undefined'
        ? new AbortController() : null;
      this.aktiveAnfrage = controller;
      let timeout = null;
      const timeoutAnfrage = new Promise((resolve, reject) => {
        timeout = setTimeout(() => {
          if (controller) controller.abort();
          const timeoutFehler = new Error('Zeitüberschreitung');
          timeoutFehler.anfrageTimeout = true;
          reject(timeoutFehler);
        }, 30000);
      });
      try {
        const serverAnfrage = fetch(
          `${process.env.VUE_APP_API_HOST}/400/410/dokumentnummern.php`,
          {
            method: 'POST',
            body: formular,
            cache: 'no-store',
            headers: { Accept: 'application/json' },
            signal: controller ? controller.signal : undefined,
          },
        );
        const response = await Promise.race([serverAnfrage, timeoutAnfrage]);
        if (this.wirdZerstoert) throw new Error('Komponente wurde geschlossen.');
        let ergebnis = null;
        try {
          ergebnis = await response.json();
        } catch {
          throw new Error('Der Server hat keine gültige Antwort geliefert.');
        }
        if (ergebnis && ergebnis.status === 'accessError') {
          this.$store.commit('login/backendErrorSession');
          throw new Error('Die Sitzung ist abgelaufen.');
        }
        const nummernstand = ergebnis && ergebnis.rechnungsnummernstand;
        const nummernstandVollstaendig = nummernstand
          && typeof nummernstand === 'object'
          && Object.prototype.hasOwnProperty.call(nummernstand, 'letzteNummer')
          && Object.prototype.hasOwnProperty.call(nummernstand, 'naechsteNummer')
          && Object.prototype.hasOwnProperty.call(nummernstand, 'revision');
        if (nummernstandVollstaendig) {
          this.statusUebernehmen(nummernstand);
        }
        if (!response.ok || !ergebnis || ergebnis.status !== 'success') {
          throw new Error(
            (ergebnis && ergebnis.message)
              || 'Der Rechnungsnummernstand konnte nicht verarbeitet werden.',
          );
        }
        if (!nummernstandVollstaendig) {
          throw new Error(
            'Der Server hat einen unvollständigen Rechnungsnummernstand geliefert.',
          );
        }
        return ergebnis;
      } catch (error) {
        if (this.wirdZerstoert) {
          const navigationsabbruch = new Error(
            'Anfrage beim Verlassen der Seite abgebrochen.',
          );
          navigationsabbruch.ignorieren = true;
          throw navigationsabbruch;
        }
        if (error && error.anfrageTimeout) {
          throw new Error('Die Anfrage hat zu lange gedauert. Bitte erneut versuchen.');
        }
        if (error && error.name === 'AbortError') {
          throw new Error('Die Anfrage hat zu lange gedauert. Bitte erneut versuchen.');
        }
        if (error instanceof TypeError) {
          throw new Error('Der Server konnte gerade nicht erreicht werden.');
        }
        throw error;
      } finally {
        clearTimeout(timeout);
        if (this.aktiveAnfrage === controller) this.aktiveAnfrage = null;
      }
    },
    async laden() {
      this.laedt = true;
      this.fehlermeldung = '';
      try {
        await this.anfragen('status');
      } catch (error) {
        if (error && error.ignorieren) return;
        this.fehlermeldung = error.message
          || 'Der Rechnungsnummernstand konnte nicht geladen werden.';
      } finally {
        this.laedt = false;
      }
    },
    async speichern() {
      if (!this.$refs.formular || !this.$refs.formular.validate()) return;
      if (this.laedt
        || this.speichert
        || this.revision === ''
        || this.letzteNummer === this.geladeneLetzteNummer) return;
      this.speichert = true;
      this.fehlermeldung = '';
      try {
        const ergebnis = await this.anfragen('speichern');
        this.$store.commit('main/alert', {
          typ: 'success',
          text: ergebnis.message || 'Der Rechnungsnummernstand wurde gespeichert.',
        }, { root: true });
      } catch (error) {
        if (error && error.ignorieren) return;
        this.fehlermeldung = error.message
          || 'Der Rechnungsnummernstand konnte nicht gespeichert werden.';
        this.$store.commit('main/alert', {
          typ: 'error',
          text: this.fehlermeldung,
        }, { root: true });
      } finally {
        this.speichert = false;
      }
    },
  },
  created() {
    this.laden();
  },
  beforeDestroy() {
    this.wirdZerstoert = true;
    if (this.aktiveAnfrage) this.aktiveAnfrage.abort();
  },
};
</script>
